import React, { useMemo } from "react";
import { FieldValues, UseFormRegister } from "react-hook-form";
import Button from "src/components/Button";
import Input, { InputTypes } from "src/components/Input";
import { useIsMobile } from "src/hooks/useIsMobile";
import { VALIDATION_MESSAGES } from "src/constants/validation";
import styled from "styled-components";
import { StyledForm } from "src/pages/ChangePassword";
import { colors } from "src/styles";
import { FormContainer } from "src/pages/DynamicFormList/styles";
// import CustomToast from "src/components/atoms/CustomToast";
import BackButton from "src/components/Button/BackButton";

interface TimeAndAttendanceInputFormProps {
  onSubmit: () => void;
  isValid: boolean;
  register: UseFormRegister<FieldValues>;
  loading: boolean;
  error?: string;
  errors?: any;
  labelMessage: string;
  validateEmployeeId?: any;
  validationError?: string;
  setValue?: any;
  trigger?: any;
  submitSuccess: boolean;
  isToastVisible: boolean;
  locationName: string;
  clockAction?: "clockIn" | "clockOut";
  onBack: () => void;
  checklistsData?: {
    checklistsInfo: Array<{
      id: string;
      default: boolean;
      oneClick: boolean;
      includeInEmailReport: boolean;
      startIndex: number;
      endIndex: number;
    }>;
    questions: Array<{
      label: string;
      questions: Array<{
        passAnswer: {
          condition: string;
          value: string;
        };
        radio: Array<{
          label: string;
          value: string;
        }>;
      }>;
    }>;
  };
}

export const TimeAndAttendanceInputForm: React.FC<TimeAndAttendanceInputFormProps> = ({
  onSubmit,
  register,
  isValid,
  loading,
  // labelMessage,
  validateEmployeeId,
  validationError,
  // submitSuccess,
  // isToastVisible,
  locationName,
  checklistsData,
  clockAction = "clockIn",
  onBack,
}) => {
  const { isMobile } = useIsMobile();

  // Extract relevant questions based on active tab
  const relevantQuestions = useMemo(() => {
    if (!checklistsData?.checklistsInfo || !checklistsData?.questions) {
      return [];
    }

    const checklist = checklistsData.checklistsInfo[0];
    if (!checklist) return [];

    const { startIndex, endIndex } = checklist;
    return checklistsData.questions.slice(startIndex, endIndex + 1);
  }, [checklistsData]);

  return (
    <>
      <Content>
        <ContentFrame $isMobile={isMobile}>
          <LocationTitleContainer>
            <LocationTitle>{locationName}</LocationTitle>
          </LocationTitleContainer>
          <Header>
            <HeaderContainer>
              <BackButton backHandler={onBack} additionalStyles={{ marginBottom: 0 }} />
              <HeaderText>{clockAction === "clockIn" ? "Clock In" : "Clock Out"}</HeaderText>
              <EmptyContainer />
            </HeaderContainer>
          </Header>

          <FormContainer>
            {/* <FormLabel>{labelMessage}</FormLabel> */}

            <InputForm $isMobile={isMobile}>
              <Input
                controlId="employee-id"
                type={InputTypes.text}
                label="Employee ID"
                validation={register("employeeId", {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES.required,
                  },
                  onBlur: (e) => validateEmployeeId(e.target.value),
                })}
              />

              {validationError && <ValidationError>{validationError}</ValidationError>}

              <Input
                controlId="first-name"
                type={InputTypes.text}
                label="First Name"
                validation={register("firstName", {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES.required,
                  },
                })}
                disabled={true}
              />

              <Input
                controlId="last-name"
                type={InputTypes.text}
                label="Last Name"
                validation={register("lastName", {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES.required,
                  },
                })}
                disabled={true}
              />

              {/* Render Checklist Questions */}
              {clockAction === "clockIn" && relevantQuestions.length > 0 && (
                <ChecklistSection>
                  {relevantQuestions.map((questionGroup, groupIndex) => (
                    <QuestionGroupContainer key={groupIndex}>
                      <QuestionGroupLabel>{questionGroup.label}</QuestionGroupLabel>

                      {questionGroup.questions.map((question, qIndex) => (
                        <RadioGroupContainer key={qIndex}>
                          {question.radio.map((option) => (
                            <RadioOption key={option.value}>
                              <RadioInput
                                type="radio"
                                id={`q-${groupIndex}-${qIndex}-${option.value}`}
                                value={option.value}
                                {...register(`checklist_${groupIndex}_${qIndex}`)}
                              />
                              <RadioLabel htmlFor={`q-${groupIndex}-${qIndex}-${option.value}`}>
                                {option.label}
                              </RadioLabel>
                            </RadioOption>
                          ))}
                        </RadioGroupContainer>
                      ))}
                    </QuestionGroupContainer>
                  ))}
                </ChecklistSection>
              )}
            </InputForm>

            <ButtonContainer>
              <ButtonWrapper>
                <Button
                  text={clockAction === "clockIn" ? "Clock In" : "Clock Out"}
                  type="submit"
                  disabled={!isValid}
                  loading={loading}
                  onClick={onSubmit}
                  additionalStyles={{ height: "48px" }}
                />
              </ButtonWrapper>
            </ButtonContainer>
          </FormContainer>
        </ContentFrame>
      </Content>

      {/* <CustomToast
        success={submitSuccess}
        isToastVisible={isToastVisible}
        title={<strong>{submitSuccess ? "Success" : "Error"}</strong>}
        message={<p>{labelMessage}</p>}
      /> */}
    </>
  );
};

// Styled Components
const Content = styled.div`
  padding-top: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  @media screen and (max-width: 1369px) {
    padding-top: 100px;
  }
`;

const LocationTitleContainer = styled.div`
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const LocationTitle = styled.h1`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #333333;
  margin: 0;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 617px;
  display: flex;
  flex-direction: column-reverse;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 617px;
`;

const HeaderText = styled.h2`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 22px;
  color: #333333;
  text-align: center;
`;

const EmptyContainer = styled.div`
  width: 40px; /* Adjust width as needed to match BackButton width */
`;

// const FormLabel = styled.p`
//   font-family: "Roboto";
//   font-style: normal;
//   font-weight: 500;
//   font-size: 20px;
//   text-align: center;
//   color: #555555;
// `;

const InputForm = styled(StyledForm)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  div {
    max-width: 617px;
    text-align: left;
  }
`;

const ContentFrame = styled.div<{ $isMobile?: boolean }>`
  padding: 30px;
  background-color: ${colors.white.default};
  box-shadow: 0px 4px 120px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "60%")};
`;

const ChecklistSection = styled.div`
  width: 100%;
  max-width: 617px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const QuestionGroupContainer = styled.div`
  margin-bottom: 20px;
`;

const QuestionGroupLabel = styled.h3`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  margin: 0 0 12px 0;
`;

const RadioGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioInput = styled.input`
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: #4caf50;

  &:hover {
    transform: scale(1.1);
  }
`;

const RadioLabel = styled.label`
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #555555;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #333333;
  }
`;

const ValidationError = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
`;
